import React from "react";
import type { ClientErrorRecoveryLabels, ClientErrorReporter } from "./clientErrorTypes";
import { buildReportedStack, formatErrorCause, readErrorRequestId, toReportableError } from "./clientErrorTypes";
import { readErrorLocation, readModuleHint } from "./clientErrorReportText";
import { ClientErrorRecoveryView } from "./ClientErrorRecoveryView";
import { createClientErrorId } from "./clientErrorIds";

type RouteErrorRecoveryViewProps = {
  currentPath?: string | null;
  labels: ClientErrorRecoveryLabels;
  routeError: unknown;
  showDeveloperDetails?: boolean;
  onBackToDashboard: () => void;
  onReload?: () => void;
  reportError: ClientErrorReporter;
};

export function RouteErrorRecoveryView({
  currentPath,
  labels,
  routeError,
  showDeveloperDetails,
  onBackToDashboard,
  onReload,
  reportError,
}: RouteErrorRecoveryViewProps) {
  const errorId = React.useRef(createClientErrorId("ROUTE"));
  const error = React.useMemo(() => toReportableError(routeError), [routeError]);
  const errorLocation = readErrorLocation(error.stack);
  const reportInput = {
    componentStack: null,
    currentPath: currentPath || "/",
    errorCause: formatErrorCause(error),
    errorId: errorId.current,
    errorLocation,
    errorMessage: error.message,
    moduleHint: readModuleHint(errorLocation),
    requestId: readErrorRequestId(error),
    stack: error.stack ?? null,
  };

  React.useEffect(() => {
    reportError({
      errorId: errorId.current,
      message: error.message || "Route error",
      sourceKind: "RouteError",
      stack: buildReportedStack(error),
    });
  }, [error, reportError]);

  return (
    <ClientErrorRecoveryView
      errorMessage={null}
      labels={labels}
      mode="page"
      reportInput={reportInput}
      showDeveloperDetails={showDeveloperDetails}
      stack={error.stack ?? null}
      onBackToDashboard={onBackToDashboard}
      onReload={onReload}
    />
  );
}
